// Pure logic helpers for the Repair tab body. Phase 5 Step 18.
//
// Lives in its own file (no PixiJS imports) so the server-side vitest
// harness — which runs inside workerd and can't pull the pixi.js
// runtime — can import these directly. market-repair-tab.ts re-exports
// them through static methods on MarketRepairTab.

import type { InventorySlot } from "../../shared/messages.ts";
import { balance } from "../../shared/balance.ts";

const REPAIR_KIT_ITEM_ID = "repair_kit"; // contracts:allow-literal — catalog id

export type RepairTabSnapshot = {
  inventory: readonly InventorySlot[];
  playerCredits: number;
};

export type RepairDisable = "insufficient_kits" | "insufficient_credits" | null;

// Filter the inventory snapshot to rows the Repair tab should render.
// Stackable / non-durability rows lack the optional fields entirely
// (server-side `durability_max === -1` sentinel) so the predicate also
// gates on rowid presence.
export function repairableRows(inventory: readonly InventorySlot[]): InventorySlot[] {
  return inventory.filter(
    (s) =>
      s.rowid !== undefined &&
      s.durabilityMax !== undefined &&
      s.durabilityCurrent !== undefined &&
      s.durabilityMax > 0 &&
      s.durabilityCurrent < s.durabilityMax,
  );
}

// Tally the player's repair-kit count across all stacks so the Repair
// button + summary line can reflect totals without re-deriving in two
// places.
export function countRepairKits(inventory: readonly InventorySlot[]): number {
  let total = 0;
  for (const s of inventory) {
    if (s.itemId === REPAIR_KIT_ITEM_ID) total += s.count;
  }
  return total;
}

// Returns null if the player can afford a repair, otherwise the code
// identifying which side is short. Used both for the disabled-button
// state and for the inline help text on the row.
export function repairDisableReason(snap: RepairTabSnapshot): RepairDisable {
  const kits = countRepairKits(snap.inventory);
  if (kits < balance.combat.repairMaterialCost) return "insufficient_kits";
  if (snap.playerCredits < balance.combat.repairCreditCost) return "insufficient_credits";
  return null;
}
